import { Component } from '@theme/component';

/** Maps every Brazilian UF to its region so a CEP lookup can resolve straight to a shipping rule. */
const UF_TO_REGION = {
  AC: 'norte',
  AP: 'norte',
  AM: 'norte',
  PA: 'norte',
  RO: 'norte',
  RR: 'norte',
  TO: 'norte',
  AL: 'nordeste',
  BA: 'nordeste',
  CE: 'nordeste',
  MA: 'nordeste',
  PB: 'nordeste',
  PE: 'nordeste',
  PI: 'nordeste',
  RN: 'nordeste',
  SE: 'nordeste',
  DF: 'centro_oeste',
  GO: 'centro_oeste',
  MT: 'centro_oeste',
  MS: 'centro_oeste',
  ES: 'sudeste',
  MG: 'sudeste',
  RJ: 'sudeste',
  SP: 'sudeste',
  PR: 'sul',
  RS: 'sul',
  SC: 'sul',
};

/**
 * @typedef {Object} RegionRule
 * @property {boolean} enabled
 * @property {string} price
 * @property {string} prazo
 */

/**
 * @typedef {Object} CalcFreteRefs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} cepInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} result
 * @property {HTMLElement} error
 */

/**
 * A custom element that resolves a CEP to a Brazilian region (via the free, keyless ViaCEP
 * lookup) and displays the shipping price/timeframe the merchant configured for that region in
 * the block's settings. It never calls Yampi's (or Shopify's) shipping engines directly — Yampi
 * only exposes an authenticated, merchant-side API for shipping rules, which cannot be called
 * safely from public storefront code (see `snippets/YampiSnippet.liquid`).
 *
 * @extends {Component<CalcFreteRefs>}
 */
class CalcFreteComponent extends Component {
  requiredRefs = ['form', 'cepInput', 'submitButton', 'result', 'error'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Record<string, RegionRule> | null} */
  #regions = null;

  /** @type {Set<string> | null} */
  #freeShippingStates = null;

  /**
   * Keeps the CEP input formatted as `00000-000` while the customer types.
   * @param {InputEvent} event
   */
  handleCepInput = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const digits = input.value.replace(/\D/g, '').slice(0, 8);
    input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  /**
   * @param {SubmitEvent} event
   */
  calculateShipping = async (event) => {
    event.preventDefault();

    const { cepInput, submitButton, result, error } = this.refs;
    const cep = cepInput.value.replace(/\D/g, '');

    this.#hide(result);
    this.#hide(error);

    if (cep.length !== 8) {
      this.#showError(this.dataset.invalidCepText ?? 'CEP inválido.');
      return;
    }

    if (this.#activeFetch) this.#activeFetch.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const buttonDefaultText = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = this.dataset.loadingText ?? 'Calculando...';

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`, { signal: abortController.signal });
      if (!response.ok) throw new Error(`ViaCEP request failed with status ${response.status}`);

      const data = await response.json();
      if (data.erro) {
        this.#showError(this.dataset.notFoundText ?? 'CEP não encontrado.');
        return;
      }

      const region = UF_TO_REGION[data.uf];
      const rule = region ? this.#getRegions()[region] : undefined;

      if (!rule) {
        this.#showError(this.dataset.errorText ?? 'Não foi possível calcular o frete agora.');
        return;
      }

      if (!rule.enabled) {
        this.#showResult(this.dataset.regionDisabledText ?? 'No momento não entregamos nessa região.');
        return;
      }

      const price = this.#getFreeShippingStates().has(data.uf)
        ? this.dataset.freeShippingPriceText || rule.price
        : rule.price;

      this.#showResult(`${this.#formatLocation(data)}: ${price} · ${rule.prazo}`);
    } catch (fetchError) {
      if (fetchError instanceof Error && fetchError.name === 'AbortError') return;
      console.warn('[calc-frete] Failed to look up CEP:', fetchError);
      this.#showError(this.dataset.errorText ?? 'Não foi possível calcular o frete agora. Tente novamente.');
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      submitButton.disabled = false;
      submitButton.textContent = buttonDefaultText;
    }
  };

  /**
   * @param {{ localidade?: string; uf?: string }} data
   */
  #formatLocation(data) {
    return data.localidade && data.uf ? `${data.localidade}/${data.uf}` : 'Sua região';
  }

  /**
   * @returns {Record<string, RegionRule>}
   */
  #getRegions() {
    if (!this.#regions) {
      try {
        this.#regions = JSON.parse(this.dataset.regions ?? '{}');
      } catch {
        this.#regions = {};
      }
    }

    return this.#regions;
  }

  /**
   * @returns {Set<string>}
   */
  #getFreeShippingStates() {
    if (!this.#freeShippingStates) {
      this.#freeShippingStates = new Set(
        (this.dataset.freeShippingStates ?? '')
          .split(',')
          .map((uf) => uf.trim().toUpperCase())
          .filter(Boolean)
      );
    }

    return this.#freeShippingStates;
  }

  /** @param {string} message */
  #showResult(message) {
    const { result } = this.refs;
    result.textContent = message;
    result.hidden = false;
  }

  /** @param {string} message */
  #showError(message) {
    const { error } = this.refs;
    error.textContent = message;
    error.hidden = false;
  }

  /** @param {HTMLElement} element */
  #hide(element) {
    element.hidden = true;
    element.textContent = '';
  }
}

if (!customElements.get('calc-frete-component')) {
  customElements.define('calc-frete-component', CalcFreteComponent);
}
